<template>
    <div class="modalBg d-flex flex-column align-items-center justify-content-center"
        @click.self="handleBackgroundClick">
        <div class="modalContent p-3">
            <!-- Title -->
            <h3 class="text-center bold mb-0">Create invoice</h3>
            <span class="d-flex justify-content-center subtitle">{{ selectedDocumentType ? selectedDocumentType.label :
                '' }}</span>

            <div v-if="getIsDownloadingInvoiceResume" class="d-flex w-100 justify-content-center my-4">
                <loader></loader>
            </div>

            <div v-else class="mt-3 invoice-to-toggle">
                <button @click="setInvoiceTo('customer')" :class="{ active: invoiceTo == 'customer' }"
                    class="invoice-to-toggle__btn">Customer</button>

                <button @click="setInvoiceTo('agent')" :class="{ active: invoiceTo == 'agent' }"
                    class="invoice-to-toggle__btn" :disabled="!enableAgentBtn">Agent</button>
            </div>

            <div v-if="invoiceTo" class="amount-card mt-3 mb-4">
                <div class="amount-card__item">
                    <div class="amount-card__label">
                        Amount to invoice<span v-if="isDocTypeInvoice" class="color-6">*</span>
                    </div>
                    <div class="amount-card__value amount-card__value--amount">{{ formatPrice(valueToInvoice) }} €</div>
                </div>

                <div class="amount-card__item">
                    <div class="amount-card__label">Due</div>
                    <div class="amount-card__value" :class="{ 'color-3': invalidInvoiceValue }">{{
                        formatPrice(valueToInvoice) }} €</div>
                </div>

                <div class="amount-card__item">
                    <div class="amount-card__label">Total</div>
                    <div class="amount-card__value">{{ formatPrice(transactionTotal) }} €</div>
                </div>
            </div>

            <!-- Billing Info Form -->
            <div v-if="invoiceTo && !getIsPrimaveraSettingsLoading" class="row mt-4">
                <div class="col-sm-6">
                    <label>Document type</label>

                    <v-select v-if="!getIsDocumentTypesLoading" :options="documentTypesOptions" label="label"
                        :value="selectedDocumentType" :clearable="false" :disabled="isSubmittingInvoice"
                        @input="onDocumentTypeChange" />

                    <loader v-else></loader>
                </div>

                <div class="col-sm-6" :class="{ error: $v.invoiceToForm.businessName.$invalid }">
                    <div class="d-flex justify-content-between">
                        <label>
                            Invoice name <span class="color-6">*</span>
                        </label>

                        <div v-if="$v.invoiceToForm.businessName.$invalid" class="messages fs-smaller">
                            <span v-if="!$v.invoiceToForm.businessName.required">Required</span>
                        </div>
                    </div>

                    <input type="text" class="full" v-model="invoiceToForm.businessName"
                        :disabled="isSubmittingInvoice">
                </div>

                <div class="col-sm-12 mt-3" :class="{ error: $v.invoiceToForm.vat.$invalid }">
                    <div class="d-flex justify-content-between">
                        <label>
                            VAT number <span v-if="isDocTypeInvoice" class="color-6">*</span>
                        </label>

                        <div v-if="$v.invoiceToForm.vat.$invalid" class="messages fs-smaller">
                            <span v-if="!$v.invoiceToForm.vat.required">Required</span>
                        </div>
                    </div>

                    <input type="text" class="full" v-model="invoiceToForm.vat" :disabled="isSubmittingInvoice"
                        @input="$v.invoiceToForm.vat.$touch">
                </div>

                <div class="col-sm-12 mt-3" :class="{ error: $v.invoiceToForm.address.$invalid }">
                    <div class="d-flex justify-content-between">
                        <label>
                            Address <span v-if="isDocTypeInvoice" class="color-6">*</span>
                        </label>

                        <div v-if="$v.invoiceToForm.address.$invalid" class="messages fs-smaller">
                            <span v-if="!$v.invoiceToForm.address.required">Required</span>
                        </div>
                    </div>

                    <input type="text" class="full" v-model="invoiceToForm.address" :disabled="isSubmittingInvoice">
                </div>

                <div class="col-sm-4 mt-3" :class="{ error: $v.invoiceToForm.city.$invalid }">
                    <div class="d-flex justify-content-between">
                        <label>
                            City <span v-if="isDocTypeInvoice" class="color-6">*</span>
                        </label>

                        <div v-if="$v.invoiceToForm.city.$invalid" class="messages fs-smaller">
                            <span v-if="!$v.invoiceToForm.city.required">Required</span>
                        </div>
                    </div>

                    <input type="text" class="full" v-model="invoiceToForm.city" :disabled="isSubmittingInvoice">
                </div>

                <div class="col-sm-4 mt-3" :class="{ error: $v.invoiceToForm.postalCode.$invalid }">
                    <div class="d-flex justify-content-between">
                        <label>
                            Postal Code <span v-if="isDocTypeInvoice" class="color-6">*</span>
                        </label>

                        <div v-if="$v.invoiceToForm.postalCode.$invalid" class="messages fs-smaller">
                            <span v-if="!$v.invoiceToForm.postalCode.required">Required</span>
                        </div>
                    </div>

                    <input type="text" class="full" v-model="invoiceToForm.postalCode" :disabled="isSubmittingInvoice">
                </div>

                <div class="col-sm-4 mt-3" :class="{ errorSelect: $v.invoiceToForm.country.$invalid }">
                    <div class="d-flex justify-content-between">
                        <label>
                            Country <span v-if="isDocTypeInvoice" class="color-6">*</span>
                        </label>

                        <div v-if="$v.invoiceToForm.country.$invalid" class="messages fs-smaller error">
                            <span v-if="!$v.invoiceToForm.country.required">Required</span>
                        </div>
                    </div>

                    <country-picker :openDirection="'top'" :selectedCountryValue="selectedCountry" :disabled="false"
                        :allowEmpty="false" @input="updateCountry($event)"></country-picker>
                </div>
            </div>

            <div v-if="getIsPrimaveraSettingsLoading" class="d-flex justify-content-center align-items-center">
                <loader size="big"></loader>
            </div>

            <!-- Buttons -->
            <div v-if="invoiceTo" class="mt-5 text-right d-flex w-100 justify-content-end align-items-center">
                <button class="btn tertiary" :disabled="isSubmittingInvoice" @click="closeModal">Cancel</button>

                <loading-button class="btn ml-2"
                    :disabled="invalidInvoiceValue || isCreatingAndPrinting || getIsPrimaveraSettingsLoading || $v.$invalid"
                    :loading="isCreating"
                    @click="createInvoice(false)">Create</loading-button>

                <loading-button class="btn ml-2"
                    :disabled="invalidInvoiceValue || isCreating || getIsPrimaveraSettingsLoading || $v.$invalid"
                    :loading="isCreatingAndPrinting"
                    @click="createInvoice(true)">Create and Print</loading-button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { required, requiredIf } from 'vuelidate/lib/validators'

import extendedVSelect from '@/extends/vue-select'
import SellerPicker from '@/tools/SellerPicker'
import Loader from '@/tools/loaders/loader1'
import CountryPicker from '@/tools/CountryPicker'
import LoadingButton from '@/tools/buttons/LoadingButton'

export default {
    data() {
        return {
            printInvoiceCheck: false,
            isCreating: false,
            isCreatingAndPrinting: false,
            invoiceTo: null,
            selectedCountry: null,
            selectedAgent: null,
            invoiceToForm: {
                vat: null,
                city: null,
                country: null,
                businessName: null,
                address: null,
                postalCode: null
            },
            invoiceResume: null,
            selectedDocumentType: null
        }
    },
    computed: {
        ...mapGetters([
            'getCountries',
            'getAgents',
            'getIsDownloadingInvoiceResume',
            'getValueToAskMoreBillingInfo',
            'account',
            'getSelectedDocumentType',
            'getDocumentTypes',
            'getIsDocumentTypesLoading',
            'getIsPrimaveraSettingsLoading',
            'getSelectedReservation'
        ]),
        isSubmittingInvoice() {
            return this.isCreating || this.isCreatingAndPrinting;
        },
        documentTypesOptions() {
            return this.getDocumentTypes.filter(i => i.id !== 'PF' && i.id !== 'NC');
        },
        isDocTypeInvoice() {
            return this.selectedDocumentType && this.selectedDocumentType.id === 'FA';
        },
        enableAgentBtn() {
            return this.reservation.creatorAgent || (this.invoiceResume && this.invoiceResume.isOTA);
        },
        valueToInvoice() {
            if (this.getIsDownloadingInvoiceResume || !this.invoiceResume) {
                return false;
            }

            if (this.invoiceTo == 'customer') {
                return this.invoiceResume.invoiceRequestCustomer.total;
            } else if (this.invoiceTo == 'agent') {
                return this.invoiceResume.invoiceRequestAgent.total;
            }
        },
        invalidInvoiceValue() {
            return this.valueToInvoice <= 0;
        }
    },
    validations: {
        invoiceToForm: {
            vat: {
                required: requiredIf(function () { return this.isDocTypeInvoice })
            },
            city: {
                required: requiredIf(function () { return this.isDocTypeInvoice })
            },
            country: {
                required: requiredIf(function () { return this.isDocTypeInvoice })
            },
            businessName: {
                required
            },
            address: {
                required: requiredIf(function () { return this.isDocTypeInvoice })
            },
            postalCode: {
                required: requiredIf(function () { return this.isDocTypeInvoice })
            }
        }
    },
    props: {
        reservation: {
            type: Object
        },
        paymentId: {
            type: Number,
            required: false
        },
        transactionTotal: {
            type: Number,
            required: false
        },
        transactionCode: {
            type: String,
            required: false
        }
    },
    async created() {
        if (this.getDocumentTypes.length === 0) {
            await this.requestDocumentTypes();
        }

        this.selectedDocumentType = this.getSelectedDocumentType
            || this.getDocumentTypes.find(item => item.isDefault)
            || this.documentTypesOptions[0];
        this.setSelectedDocumentType(this.selectedDocumentType);

        this.downloadInvoiceResume();
    },
    methods: {
        ...mapActions([
            'downloadReservationInvoiceResume',
            'emitInvoice',
            'updateExistingReservation',
            'printInvoice',
            'requestPrimaveraSettings',
            'requestDocumentTypes',
            'setSelectedDocumentType',
            'refreshCurrentSelectedReservation'
        ]),
        downloadInvoiceResume() {
            return this.downloadReservationInvoiceResume({
                reservationId: this.reservation.id,
                docType: this.selectedDocumentType.id,
                paymentId: this.paymentId
            }).then(response => {
                this.invoiceResume = response.data;

                if (!this.invoiceTo && !this.enableAgentBtn) {
                    this.setInvoiceTo('customer');
                } else if (this.invoiceTo) {
                    this.fillInvoiceForm(this.invoiceTo);
                }
            }).catch(() => {
                this.closeModal();
            });
        },
        onDocumentTypeChange(documentType) {
            if (!documentType) return;

            this.selectedDocumentType = documentType;
            this.setSelectedDocumentType(documentType);
        },
        handleBackgroundClick(event) {
            if (this.isSubmittingInvoice) {
                return;
            }

            const selectedText = window.getSelection().toString();

            if (selectedText.length > 0) {
                return;
            } else {
                this.closeModal();
            }
        },
        closeModal() {
            this.$emit('close');
        },
        setInvoiceTo(invoiceTypeSelected) {
            this.invoiceTo = invoiceTypeSelected;

            this.fillInvoiceForm(invoiceTypeSelected);
        },
        fillInvoiceForm(invoiceTypeSelected) {
            const invoiceType = invoiceTypeSelected === 'customer' ? 'invoiceRequestCustomer' : 'invoiceRequestAgent';
            const invoice = this.invoiceResume[invoiceType].cliente;

            if (invoice) {
                this.invoiceToForm = {
                    id: invoice.id || 0,
                    vat: invoice.taxNumber || '',
                    city: invoice.location || '',
                    country: invoice.countryId === 0 ? null : invoice.countryId,
                    businessName: invoice.name || '',
                    address: invoice.address || '',
                    postalCode: invoice.postalCode || '',
                }

                this.setCountry(invoice.countryId);
            }
        },
        createInvoice(isToPrint) {
            let self = this;

            if (this.invoiceTo === 'agent' &&
                (this.invoiceToForm.vat === null || this.invoiceToForm.vat === '') &&
                (this.invoiceToForm.businessName === null || this.invoiceToForm.businessName === '')) {

                return this.$dialog.confirm('\
                    <div class="fs-larg font-weight-600 mb-4">Incomplete Invoice Information</div>\
                    <div>The invoice information is incomplete. Please update the information to proceed.</div>', {
                    customClass: 'cancelGreen okBlue cancelWide alignRight',
                    reverse: true,
                    okText: 'Invoice to End Customer',
                    cancelText: 'OK'
                })
                    .then(function () {
                        self.requestPrimaveraSettings().then(data => {
                            if (data) {
                                self.invoiceToForm.vat = data.defaultEndCustomerTaxNumber;
                                self.invoiceToForm.businessName = data.defaultEndCustomerName;
                            }
                        });
                    })
                    .catch(err => { });
            } else {
                this.requestInvoiceEmition(isToPrint);
            }
        },
        async requestInvoiceEmition(isToPrint) {
            let invoiceTarget;

            if (this.invoiceTo === 'customer') {
                //is customer
                invoiceTarget = 0;
            } else {
                if (this.invoiceResume.isOTA) {
                    // is ota
                    invoiceTarget = 2;
                } else {
                    // is agent
                    invoiceTarget = 1;
                }
            }

            if (isToPrint) {
                this.isCreatingAndPrinting = true;
            } else {
                this.isCreating = true;
            }

            try {
                const response = await this.emitInvoice({
                    reservationId: this.reservation.id,
                    invoiceTargetType: invoiceTarget,
                    documentType: this.selectedDocumentType.id,
                    client: this.invoiceToForm,
                    paymentId: this.paymentId,
                    transactionCode: this.transactionCode
                });

                if (response.status != 200) {
                    const message = (response.data && response.data.error) || 'Failed to create invoice';
                    this.$toasted.error(message);
                    return;
                }

                // Show success toast
                this.$toasted.success('Invoice created successfully. Updating reservation...');

                // Save reservation
                try {
                    const updateResponse = await this.updateExistingReservation({
                        supplierId: this.account.supplierId,
                        reservation: this.reservation
                    });

                    if (updateResponse.status && updateResponse.status == 200) {
                        this.reservation.updateFlags = {};
                    }
                } catch (err) {
                    this.$toasted.error('Failed to update reservation');
                }

                // Open invoice pdf in a new tab ("Create and Print" button)
                if (isToPrint) {
                    try {
                        const printResponse = await this.printInvoice(response.data.id);
                        if (printResponse.status == 200) {
                            this.openByteArrayNewTab(printResponse.data);
                        }
                    } catch (err) {
                        this.$toasted.error('Failed to print invoice');
                    }
                }

                // Only close once the reservation has fully refreshed, so the invoice list is up to date
                await this.refreshCurrentSelectedReservation();
                this.eventHub.$emit('updateThisReservation', this.getSelectedReservation);

                this.$emit('created');
                this.closeModal();
            } catch (err) {
                this.$toasted.error('Failed to create invoice');
            } finally {
                this.isCreating = false;
                this.isCreatingAndPrinting = false;
            }
        },
        updateCountry(e) {
            this.selectedCountry = e;

            if (e) {
                this.invoiceToForm.country = e.id;
            } else {
                this.invoiceToForm.country = null;
            }
        },
        setCountry(countryId) {
            if (!countryId && this.invoiceToForm.country) {
                countryId = this.invoiceToForm.country;
            }

            if (typeof countryId == "object") {
                countryId = countryId.id;
            }

            if (countryId === undefined) {
                this.selectedCountry = null;

                return false;
            }

            this.selectedCountry = this.getCountries.filter(country => {
                return country.id == countryId;
            })[0]
        }
    },
    components: {
        vSelect: extendedVSelect,
        SellerPicker,
        Loader,
        CountryPicker,
        LoadingButton
    }
}
</script>

<style lang="scss" scoped>
@import '../../assets/scss/_variables.scss';

.modalBg {
    z-index: 10;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);

    .modalContent {
        width: 700px;
        max-width: 90%;
        max-height: 95vh;
        overflow-y: auto;
        background-color: #FFF;

        /* Hide scrollbar while keeping content scrollable */
        -ms-overflow-style: none;
        /* IE / Edge */
        scrollbar-width: none;
        /* Firefox */

        &::-webkit-scrollbar {
            /* Chrome / Safari */
            display: none;
        }
    }
}

.subtitle {
    font-size: 13px;
    color: $gray-dark;
    margin-top: 4px;
}

.invoice-to-toggle {
    display: flex;
    gap: 8px;

    border-radius: 8px;
    padding: 4px;

    &__btn {
        flex: 1;
        border: none;
        background-color: $gray-lighter;
        color: $gray-darker;
        font-weight: 600;
        padding: 10px 0;
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.15s ease;

        &.active {
            background-color: $blue-bright;
            color: #FFF;
        }

        &:disabled {
            opacity: 0.4;
            cursor: not-allowed;
        }
    }
}

.amount-card {
    display: flex;
    background-color: $gray-lighter;
    border-radius: 8px;
    overflow: hidden;

    &__item {
        position: relative;
        flex: 1;
        padding: 14px 20px;

        &+&::before {
            content: '';
            position: absolute;
            top: 16px;
            bottom: 16px;
            left: 0;
            width: 1px;
            background-color: rgba(0, 0, 0, 0.08);
        }
    }

    &__label {
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.5px;
        text-transform: uppercase;
        color: $gray-dark;
    }

    &__value {
        font-size: 22px;
        font-weight: 700;
        line-height: 1.2;
        margin-top: 6px;
        white-space: nowrap;

        &--amount {
            color: $green;
        }
    }
}

.optional-label {
    color: $gray-dark;
    font-weight: 400;
}
</style>


// WEBPACK FOOTER //
// CreateInvoice.vue?58e5ff3a